/**
 * Section Definitions Index
 *
 * Exports all built-in section definitions.
 * Sections are page-level building blocks (header, paragraph, image, video, cta)
 * as opposed to components which are nested inside layout sections.
 */

import { headerSection } from './header';
import { paragraphSection } from './paragraph';
import { imageSection } from './image';
import { videoSection } from './video';
import { ctaSection } from './cta';
import { layoutSection } from './layout';

/**
 * All built-in section definitions
 */
export const builtInSections = [
  headerSection,
  paragraphSection,
  imageSection,
  videoSection,
  ctaSection,
  layoutSection,
];

/**
 * Section registry map
 * @type {Map<string, SectionDefinition>}
 */
const sectionRegistry = new Map();

// Register all built-in sections
builtInSections.forEach((section) => {
  if (section) {
    sectionRegistry.set(section.type, section);
  }
});

/**
 * Get a section definition by type
 * @param {string} type - Section type identifier
 * @returns {SectionDefinition|undefined}
 */
export function getSectionDefinition(type) {
  return sectionRegistry.get(type);
}

/**
 * Get all registered section definitions
 * @returns {SectionDefinition[]}
 */
export function getAllSectionDefinitions() {
  return Array.from(sectionRegistry.values());
}

/**
 * Check if a section type is registered
 * @param {string} type - Section type to check
 * @returns {boolean}
 */
export function hasSection(type) {
  return sectionRegistry.has(type);
}

/**
 * Get settings schema for a section type
 * @param {string} type - Section type
 * @returns {SettingsField[]|null}
 */
export function getSectionSettingsSchema(type) {
  const def = getSectionDefinition(type);
  return def?.settingsSchema || null;
}

/**
 * Get default content for a section type
 * @param {string} type - Section type
 * @returns {Object|null}
 */
export function getSectionDefaultContent(type) {
  const def = getSectionDefinition(type);
  return def?.defaultContent || null;
}

// Re-export individual section definitions for direct import
export {
  headerSection,
  paragraphSection,
  imageSection,
  videoSection,
  ctaSection,
  layoutSection,
};

export default sectionRegistry;
